import { AdminTierTypeEnum } from '../../../services/drillDown';
import { PricebookBundle, AvailablePricebooks } from '../../../models/PricebookData';
import { PaidByEnum } from '../../../models/LicenseData';

export type RestrictionMode = 'inherit' | 'override' | 'advanced';

export const getRestrictionMode = (
  adminTier: AdminTierTypeEnum,
  availablePricebooks: AvailablePricebooks | undefined,
  parentAvailablePricebooks?: AvailablePricebooks | undefined,
): RestrictionMode => {
  if (availablePricebooks?.tier?.toUpperCase() !== adminTier.toUpperCase()) {
    return 'inherit';
  }

  if (parentAvailablePricebooks) {
    const parentIds = new Set(
      (parentAvailablePricebooks.pricebookBundleDtos ?? []).map(pb => pb.pricebook.id),
    );
    const hasNonParentIds = (availablePricebooks.pricebookBundleDtos ?? []).some(
      pb => !parentIds.has(pb.pricebook.id),
    );
    if (hasNonParentIds) {
      return 'advanced';
    }
  }

  return 'override';
};

export const getSubmittedPricebookIds = (
  mode: RestrictionMode,
  selectedPricebookIds: number[] | undefined,
): number[] | undefined => (mode === 'inherit' ? undefined : (selectedPricebookIds ?? []));

export const getParentTierLabel = (
  adminTier: AdminTierTypeEnum,
  availablePricebooks: AvailablePricebooks | undefined,
): string => {
  switch (adminTier) {
    case AdminTierTypeEnum.Organization:
      return (availablePricebooks?.tier ?? AdminTierTypeEnum.Team).toLowerCase();
    case AdminTierTypeEnum.Team:
      return AdminTierTypeEnum.Group.toLowerCase();
    case AdminTierTypeEnum.Group:
    case AdminTierTypeEnum.Root:
      return '';
  }
};

export const sortPricebooksByPayerAndName = (bundles: PricebookBundle[]): PricebookBundle[] => {
  return [...bundles].sort((a, b) => {
    const aIsMemberPays = a.pricebook.paidBy === PaidByEnum.User;
    const bIsMemberPays = b.pricebook.paidBy === PaidByEnum.User;

    if (aIsMemberPays && !bIsMemberPays) return -1;
    if (!aIsMemberPays && bIsMemberPays) return 1;

    return a.pricebook.pricebookName.localeCompare(b.pricebook.pricebookName);
  });
};

export const sortPricebooksByInUseAndName = (
  bundles: PricebookBundle[],
  inUsePricebookIds: Set<number>,
): PricebookBundle[] => {
  return [...bundles].sort((a, b) => {
    const aInUse = inUsePricebookIds.has(a.pricebook.id);
    const bInUse = inUsePricebookIds.has(b.pricebook.id);

    if (aInUse && !bInUse) return -1;
    if (!aInUse && bInUse) return 1;

    return a.pricebook.pricebookName.localeCompare(b.pricebook.pricebookName);
  });
};

export const getFooterMessage = (
  mode: RestrictionMode,
  selectedCount: number,
  parentTierLabel: string,
  tierLabel: string,
): { text: string; boldSegment?: string } => {
  if (mode === 'inherit') {
    const inheritLabel = parentTierLabel
      ? `Inherited ${parentTierLabel} pricebook restrictions applied.`
      : 'Default pricebook restrictions applied.';
    return { text: inheritLabel };
  }

  if (selectedCount === 0) {
    return { text: `No ${tierLabel} pricebook restrictions applied.` };
  }

  if (selectedCount === 1) {
    return { text: "You've selected 1 pricebook.", boldSegment: '1' };
  }

  return {
    text: `You've selected ${selectedCount} pricebooks.`,
    boldSegment: `${selectedCount}`,
  };
};
